import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { HttpSecurityObjects, httpClient } from "../http";
import type { SecurityObjectPermission } from "../types/security";

export type SecurityObjectDictionaryByCode = Record<string, SecurityObjectPermission>;
export type SecurityObjectDictionaryByRoute = Record<string, SecurityObjectPermission>;

type SecurityObjectContextValue = {
    byCode: SecurityObjectDictionaryByCode | null;
    byRoute: SecurityObjectDictionaryByRoute | null;
    reload: () => Promise<void>;
};

export const SecurityObjectContext = createContext<SecurityObjectContextValue>({
    byCode: null,
    byRoute: null,
    reload: async () => {},
});

const normalizePath = (p: string) => {
    const raw = (p ?? "").trim();
    if (!raw) return "/";
    const withSlash = raw.startsWith("/") ? raw : `/${raw}`;
    const noTrailing = withSlash.length > 1 ? withSlash.replace(/\/+$/, "") : withSlash;
    return noTrailing;
};

const buildIndexes = (items: SecurityObjectPermission[]) => {
    const byCode: SecurityObjectDictionaryByCode = {};
    const byRoute: SecurityObjectDictionaryByRoute = {};

    for (const item of items) {
        byCode[item.codigoObjetoSeguridad] = item;

        if (item.rutaVirtual) {
            const key = normalizePath(item.rutaVirtual);

            byRoute[key] = item;
        }
    }

    return { byCode, byRoute };
};

export function SecurityObjectProvider({ children }: { children: React.ReactNode }) {
    const [byCode, setByCode] = useState<SecurityObjectDictionaryByCode | null>(null);
    const [byRoute, setByRoute] = useState<SecurityObjectDictionaryByRoute | null>(null);

    const load = useCallback(async () => {
        if (!httpClient.getAccessToken()) {
            setByCode({});
            setByRoute({});
            return;
        }

        try {
            const list = await HttpSecurityObjects.getAll();
            const { byCode, byRoute } = buildIndexes(list);
            setByCode(byCode);
            setByRoute(byRoute);
        } catch {
            // Fail-closed
            setByCode({});
            setByRoute({});
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const value = useMemo(() => ({ byCode, byRoute, reload: load }), [byCode, byRoute, load]);

    return (
        <SecurityObjectContext.Provider value={value}>{children}</SecurityObjectContext.Provider>
    );
}

export { normalizePath };
